import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import classnames from 'classnames';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { User } from '../../types/user.type';
import { Petition } from '../../types/petition.type';
import { userSdk } from '../../services/user.service';
import ResponseCodes from '../../constants/response-codes.constant';
import PetitionStatusList from '../../components/petition-status-list/petition-status-list';
import { useAuthStore, useOperatorStore, usePetitionStore } from '../../store';
import GoBack from '../../components/go-back/go-back';
import { Operator, RolePermission } from '../../types/operator.type';
import Divider from '../../components/divider/divider';
import View from '../../components/view/view';
import { useSettingsStore } from '../../store/settings-store';
import { petitionSdk } from '../../services/petition.service';
import { PetitionComment } from '../../types/petition-comment.type';
import { hasPermissions } from '../../utils/permissions';

import styles from './petition-details.module.scss';
import AddComment from './components/add-comment/add-comment';
import CommentsList from './containers/comments-list/comments-list';

const PetitionDetailsView = () => {
    const { ['id']: petitionId } = useParams();
    const navigate = useNavigate();

    const { theme, sendOnEnter } = useSettingsStore();
    const petitionState = usePetitionStore();
    const { operators } = useOperatorStore();
    const { permissions, accessToken, profile } = useAuthStore();

    const [petition, setPetition] = useState<Petition | null>(null);
    const [comments, setComments] = useState<PetitionComment[]>([]);
    const [user, setUser] = useState<User | undefined>(undefined);
    const [petitionOperators, setPetitionOperators] = useState<Operator[]>([]);

    useEffect(() => {
        fetchPetition(permissions);
    }, [permissions]);

    useEffect(() => {
        if (!petition) return;

        getUserInfo(petition.user!.id, permissions);
        getOperatorsDetails(petition.operators!, permissions);
        setComments(petition.comments ?? []);
    }, [petition, permissions]);

    const fetchPetition = async (permissions: RolePermission[]) => {
        if (!hasPermissions('read-petitions', permissions)) return null;
        setPetition(await petitionState.fetchById(Number(petitionId)));
    };

    const getUserInfo = async (
        userId: number,
        permissions: RolePermission[]
    ) => {
        if (!hasPermissions('read-petitions', permissions)) return null;
        const response = await userSdk.getUserById(userId);
        if (response.code !== ResponseCodes.PROCESS_OK) {
            console.error('[!] Error getting user info:', response);
        }
        setUser(response.data);
    };

    const getOperatorsDetails = (
        operatorIds: number[],
        permissions: RolePermission[]
    ) => {
        if (!hasPermissions('read-petitions', permissions)) return null;
        const selectedOperators = operators.filter((operator) =>
            operatorIds.includes(operator.id)
        );
        setPetitionOperators(selectedOperators);
    };

    const goToOperatorView = useCallback((id?: number) => {
        if (!id) return;
        return navigate(`/operators/${id}`);
    }, []);

    const renderAttendant: () => ReactElement = useCallback(() => {
        if (!petition?.attendedBy) return <span>N/A</span>;
        const { attendedBy } = petition;
        return (
            <a
                className={styles.cmcLink}
                onClick={() => goToOperatorView(attendedBy.id)}
            >
                {attendedBy?.firstName} {attendedBy?.lastName}
            </a>
        );
    }, [petition?.attendedBy, goToOperatorView]);

    const onCommentAuthorSelect = (id: number) => {
        return navigate(`/operators/${id}`);
    };

    const submitComment: (comment: string) => Promise<void> = useCallback(
        async (content) => {
            if (!accessToken || !profile) return;

            setComments((prev) => [
                {
                    petitionId: Number(petitionId),
                    content: content,
                    createdAt: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
                    authorId: profile.id,
                    author: {
                        id: profile.id,
                        firstName: profile.firstName,
                        lastName: profile.lastName,
                    },
                },
                ...prev,
            ]);

            await petitionSdk.addComment(
                {
                    id: Number(petitionId),
                    content: content,
                },
                accessToken
            );
        },
        [accessToken, profile]
    );

    return (
        <View
            theme={theme}
            permissions={permissions}
            requiredPermission="read-petitions"
        >
            <div className={'viewContent'} style={{ padding: '20px 40px' }}>
                <header>
                    <GoBack theme={theme.current} />
                    <h1 className={'view-title'} style={{ fontSize: 24 }}>
                        Solicitud Nº {petition?.id}
                    </h1>
                </header>
                <div>
                    <h4>Fecha de solicitud</h4>
                    <span>
                        {petition &&
                            format(
                                new Date(petition?.createdAt),
                                "MMMM dd, yyyy 'a las' hh:mm:ss aaaa",
                                { locale: es }
                            )}
                    </span>
                </div>
                <div>
                    <h4>Dirección</h4>
                    <span>
                        {petition?.referencePoint ??
                            petition?.googleReferencePoint}
                    </span>
                </div>
                <section className={styles.content}>
                    {petition?.statusHistory && (
                        <PetitionStatusList
                            statusList={petition.statusHistory}
                        />
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '300px',
                        }}
                    >
                        {user?.id && (
                            <div
                                className={classnames({
                                    [styles.infoSection]: true,
                                    [styles.userInfo]: true,
                                })}
                            >
                                <h5>Solicitado por:</h5>
                                <span>
                                    Nombre: {user.firstName} {user.lastName}
                                </span>
                                <span>
                                    Cédula: {user.documentType}-{user.document}
                                </span>
                            </div>
                        )}
                        <div
                            className={classnames({
                                [styles.infoSection]: true,
                                [styles.cmcInfo]: true,
                            })}
                        >
                            <h5>Atendida por:</h5>
                            {renderAttendant()}
                        </div>
                        <div
                            className={classnames({
                                [styles.infoSection]: true,
                                [styles.cmcInfo]: true,
                            })}
                        >
                            <h5>Operadores:</h5>
                            {petitionOperators.length > 0 ? (
                                <ul>
                                    {petitionOperators.map((operator) => (
                                        <li key={operator.id}>
                                            {operator.firstName}{' '}
                                            {operator.lastName}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <span>Ningún operador fue despachado.</span>
                            )}
                        </div>
                    </div>
                </section>
                <Divider />
                <div>
                    <h4>Comentarios:</h4>
                    <div className={styles.newCommentContainer}>
                        <AddComment
                            sendOnEnter={sendOnEnter}
                            onCreate={submitComment}
                        />
                    </div>
                    <CommentsList
                        comments={comments}
                        onSelectAuthor={onCommentAuthorSelect}
                    />
                </div>
            </div>
        </View>
    );
};

export default PetitionDetailsView;
